import { Check, ShieldCheck, Sparkles } from "lucide-react";
import bgLogo from "../../assets/bg.svg";
import type { Section } from "./types";

type Props = {
  sections: Section[];
  currentIndex: number;
  progress: number;
};

export const AssessmentSidebar = ({ sections, currentIndex, progress }: Props) => {
  const steps = [
    { id: "company", shortLabel: "Company" },
    ...sections.map((s) => ({ id: s.id, shortLabel: s.shortLabel })),
    { id: "review", shortLabel: "Review" },
  ];

  return (
    <aside className="h-full lg:sticky lg:top-8">
      <div className="flex h-full flex-col space-y-6">

        {/* Header */}
        <div className="mb-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-dark">
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-signal"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold tracking-tight text-foreground">Signal AI</span>
              <span className="inline-flex items-center gap-0">
                <span className="text-xs font-medium text-muted-foreground">by</span>
                <img src={bgLogo} alt="Gowitek" className="h-5 w-auto object-contain" />
              </span>
            </div>
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-foreground sm:text-[1.75rem] leading-tight">
            Signal AI readiness assessment
          </h2>
          <p className="mt-1 text-base text-muted-foreground">
            <span className="font-bold text-black">Start at $0.</span> No upfront commitment.
          </p>
        </div>

        {/* Progress */}
        <div className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-card)]">
          <div className="flex items-center justify-between text-xs font-medium">
            <span className="text-muted-foreground">Assessment Progress</span>
            <span className="text-foreground">{progress}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          <ol className="mt-5 space-y-2.5">
            {steps.map((s, i) => {
              const done = i < currentIndex;
              const active = i === currentIndex;
              return (
                <li key={s.id} className="flex items-center gap-3">
                  <span
                    className={[
                      "flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-semibold transition-colors",
                      done
                        ? "bg-primary text-primary-foreground"
                        : active
                        ? "bg-[hsl(var(--primary-soft))] text-primary ring-1 ring-primary"
                        : "bg-muted text-muted-foreground",
                    ].join(" ")}
                  >
                    {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                  </span>
                  <span
                    className={[
                      "text-[0.95rem]",
                      active ? "font-semibold text-foreground" : "text-muted-foreground",
                    ].join(" ")}
                  >
                    {s.shortLabel}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Start at $0 (pixel-perfect alignment) */}
        <div className="rounded-[1.25rem] bg-[#0b1b36] px-[1.6rem] py-[0.85rem] text-left w-full" style={{boxSizing:'border-box'}}>
          <br></br>
          <p className="mt-[0.35rem] text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl leading-tight">
            AI is easy.
          </p>
          <p className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl leading-tight text-right text-white">
            <span className="font-serif italic" style={{ color: '#1fb6ff' }}>ROI isn't.</span>
          </p>
          <p className="mt-[1.55rem] text-[0.99rem] leading-[1.6] text-white font-normal">
            Which is why the starting point needs to be low-risk, but high clarity.             You decide next steps based on actual outcomes.

          </p>
         
        </div>

       
      </div>
    </aside>
  );
};
